import React, { useContext, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../../context/authContext';
import usePendingBookings from '../../../hooks/usePendingBookings';

function BulkAcknowledgeAutoCheckedOutButton({
  branchName,
  bookings,
  onResolved,
  variant = 'contained',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { acknowledgeAutoCheckOutBulk } = usePendingBookings();

  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canShow =
    Array.isArray(bookings) &&
    bookings.length > 0 &&
    authCtx.checkRightAny('internalBooking:special:resolveForgotten');

  if (!canShow) return null;

  const handleOpen = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setOpen(true);
  };

  const handleClose = event => {
    event?.stopPropagation?.();
    if (isSubmitting) return;
    setOpen(false);
  };

  const handleConfirm = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    const ids = bookings.map(b => String(b?._id)).filter(Boolean);
    if (ids.length === 0) return;

    try {
      setIsSubmitting(true);
      const toastMessage = `Die Bestätigung für ${ids.length} automatisch ausgecheckte Buchungen in ${
        branchName || 'diesem Standort'
      } wurde gespeichert und kann nach Identifizierung abgeschlossen werden`;
      const successToastMessage = 'Automatische Checkouts bestätigt';
      const displayInfo = {
        Typ: 'Automatische Checkouts pro Standort bestätigen',
        Standort: branchName || '—',
        Anzahl: String(ids.length),
      };

      const summary = await acknowledgeAutoCheckOutBulk(
        ids,
        toastMessage,
        null,
        displayInfo,
        null,
        successToastMessage
      );

      if (summary) {
        const { resolved = 0, skipped = 0, failed = 0 } = summary;
        if (failed > 0) {
          toast.warn(
            `${resolved} bestätigt, ${skipped} übersprungen, ${failed} fehlgeschlagen`
          );
        } else if (skipped > 0) {
          toast.info(`${resolved} bestätigt, ${skipped} übersprungen`);
        }
      }

      if (typeof onResolved === 'function') onResolved(summary);
      setOpen(false);
    } catch (_) {
      // Errors are handled by the hook/useHttp via toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  const count = bookings.length;

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color='primary'
        fullWidth={fullWidth}
        onClick={handleOpen}
        data-ignore-card-click
        sx={{ whiteSpace: 'nowrap', ...sx }}
      >
        Zur Kenntnis genommen
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>Automatische Checkouts bestätigen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant='body2'>
              Am Standort <strong>{branchName || '—'}</strong>{' '}
              {count === 1 ? 'wurde' : 'wurden'} in der vergangenen Nacht{' '}
              <strong>{count}</strong>{' '}
              {count === 1 ? 'Buchung' : 'Buchungen'} um 02:00 Uhr automatisch vom
              System ausgecheckt, da kein manuelles Auschecken erfolgt ist.
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Die Buchungen sind bereits abgeschlossen (inklusive neutraler
              Kundenstimmung und versendeter Bewertungsmail). Mit der Bestätigung
              erklärt die Standortleitung, dass die Fälle zur Kenntnis genommen wurden
              und entsprechende Maßnahmen zur Schulung des Teams eingeleitet werden.
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Es wird <strong>kein Grund erfasst</strong> und{' '}
              <strong>kein Verantwortlicher zugewiesen</strong>. Nach der Bestätigung
              verschwinden die Einträge dieses Standortes aus dem Handlungsbedarf.
            </Typography>
            <Alert severity='info'>
              Ohne Bestätigung bleibt die Liste pro Standort bestehen und wächst mit
              jeder weiteren Nacht an.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Wird bestätigt…'
              : `${count} ${count === 1 ? 'Eintrag' : 'Einträge'} bestätigen`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BulkAcknowledgeAutoCheckedOutButton;
